<template>
  <div class="flex md:my-16 my-8 md:mx-8 text-dark-blue md:space-x-12 lg:space-x-24">
    <div class="hidden md:block w-full flex-0 basis-60 lg:basis-80 flex-shrink-0">
      <div v-if="blogDetail.author" class="flex mb-8">
        <BaseAvatar :img="blogDetail.author.image ?? ''" class="mr-2 w-12"></BaseAvatar>
        <div class="flex flex-col justify-center font-semibold">
          <NuxtLinkLocale :locale="switchBlogLocale(true)" :to="authorName">
            <div class="text-sm text-secondary">
              {{ blogDetail.author.display_name }}
            </div>
          </NuxtLinkLocale>
          <span class="text-dark-blue-light text-[13px]">{{ blogDetail.author.designation }}</span>
        </div>
      </div>
      <BaseTableOfContent :content-ref="conditionContent" :auto-focus-first="true">
        <BlogSocialSharing class="justify-center" />
      </BaseTableOfContent>
    </div>

    <div class="w-full flex-1">
      <article>
        <ul
          class="items-center flex-wrap text-secondary text-xs mb-4 space-x-4 flex"
          v-if="blogDetail.tags && blogDetail.tags.length > 0"
        >
          <li
            v-for="(tag, index) in blogDetail.tags"
            :key="index"
            class="transition duration-300 rounded border p-1 border-secondary-light cursor-pointer mb-4"
          >
            <NuxtLinkLocale :locale="switchBlogLocale(true)" :to="`/blog/tags/${tag.slug}`">
              {{ tag.name }}
            </NuxtLinkLocale>
          </li>
        </ul>
        <div class="flex-col flex md:flex-row text-blog-14 font-semibold items-center my-4">
          <div>
            <span class="text-dark-blue-light">{{ $t("blog.publishedDate", { date: blogDetail.post_date }) }}</span>
            <NuxtLinkLocale
              v-if="blogDetail.categories"
              :locale="switchBlogLocale(true)"
              :to="categoryUrl"
              class="hover:underline text-secondary pl-2 whitespace-nowrap"
            >
              {{ blogDetail.categories.name }}
            </NuxtLinkLocale>
          </div>
          <div class="hidden md:flex ml-auto space-x-2 items-center font-semibold">
            <NuxtImg loading="lazy" src="/icons/stopwatch.svg" alt="stopwatch" width="17" height="20" />
            <span class="text-dark-blue-light whitespace-nowrap">
              {{ $t("blog.readTime", { time: blogDetail.reading_time }) }}
            </span>
          </div>
        </div>

        <div class="md:hidden flex">
          <div v-if="blogDetail.author" class="flex not-md-only md:sr-only">
            <BaseAvatar :img="blogDetail.author.image ?? ''" class="mr-2 w-12"></BaseAvatar>
            <div class="flex flex-col justify-center font-semibold">
              <div class="text-sm text-secondary">
                <NuxtLinkLocale :locale="switchBlogLocale(true)" class="text-secondary" :to="authorName">
                  {{ blogDetail.author.display_name }}
                </NuxtLinkLocale>
              </div>
              <span class="text-dark-blue-light text-[13px]">{{ blogDetail.author.designation }}</span>
            </div>
          </div>
          <div class="flex ml-auto space-x-2 items-center font-semibold">
            <NuxtImg loading="lazy" src="/icons/stopwatch.svg" alt="stopwatch" width="17" height="20" />
            <span class="text-dark-blue-light text-sm">{{ blogDetail.reading_time }} {{ $t("blog.read") }}</span>
          </div>
        </div>

        <div ref="conditionContent">
          <div
            v-for="(sectionContent, index) in blogDetail.content"
            :key="index"
            class="my-8 section-content blog-content whitespace-pre-line"
          >
            <h2 :id="sectionContent.id" :class="sectionContent.id" class="text-left text-lg scroll-mt-36 mb-5">
              {{ sectionContent.title }}
            </h2>
            <div v-if="sectionContent.id == 'intro'" v-html="sectionContent.content" class="text-blog-19"></div>
            <div v-else-if="sectionContent.content" class="leading-7" v-html="sectionContent.content"></div>
            <div v-if="sectionContent.cta_block_content" class="py-8">
              <CommonCTABlock
                :blockTitle="sectionContent.cta_block_title"
                :content="sectionContent.cta_block_content"
                :backgroundImage="sectionContent.cta_block_background || ''"
                :backgroundImageAlt="sectionContent.cta_block_background_alt || ''"
                :actionButtonText="sectionContent.cta_action_button_text"
                :actionButtonLink="sectionContent.cta_block_action_button_link"
              ></CommonCTABlock>
            </div>
            <div v-if="sectionContent.youtube_url" class="py-8">
              <CommonYouTubeVideo :youtube-url="sectionContent.youtube_url"></CommonYouTubeVideo>
            </div>
            <div v-if="sectionContent.pdf_title" class="py-8">
              <CommonPdfBLock :pdfTitle="sectionContent.pdf_title" :pdfFile="sectionContent.pdf_file"></CommonPdfBLock>
            </div>
            <div v-if="sectionContent.quote" class="py-8">
              <CommonQuoteBlock
                :quote="sectionContent.quote"
                :quoteAuthor="sectionContent.quoteAuthor"
                :quoteAuthorDesignation="sectionContent.quoteAuthorDesignation"
              ></CommonQuoteBlock>
            </div>
          </div>
        </div>
      </article>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  blogDetail: {
    type: Object,
  },

  showSocialSharing: {
    type: Boolean,
    default: false,
  },
});
const conditionContent = ref(null);
const authorName = `/blog/authors/${props.blogDetail?.author?.slug ?? ""}`;
const categoryUrl = `/blog/categories/${props.blogDetail?.categories?.slug ?? ""}`;
</script>

<style scoped>
.section-link li.active {
  font-weight: bold;
}

.section-content {
  overflow: hidden;
}
</style>

<style>
.wp-caption-text {
  font-weight: 700;
  font-size: 0.875rem;
  line-height: 1.25rem;
  margin-top: 0.25rem;
}

.blog-content h3 {
  @apply font-bold text-base;
}
.blog-content h4 {
  @apply font-bold text-base mt-3 text-[#00A1AA99];
}
</style>
